import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../utils/api';
import './ContainerEditor.css';
import { ContainerTemplate, ContainerComponentRule } from '../types/containerTemplates';
import { TemplateContainer } from '../types/template';

const areaOptions = [
  { value: 'header', label: 'Шапка' },
  { value: 'main', label: 'Основная область' },
  { value: 'column', label: 'Колонка' },
  { value: 'sidebar', label: 'Сайдбар' },
  { value: 'menu', label: 'Меню' },
  { value: 'footer', label: 'Подвал' },
  { value: 'global', label: 'Глобальный блок' }
];

const scopeOptions = [
  { value: 'primary', label: 'Основные блоки' },
  { value: 'column', label: 'Колонки' },
  { value: 'universal', label: 'Универсальные' }
];

export const deriveAreaType = (containerType?: string) => {
  const normalized = (containerType || '').toLowerCase();
  if (normalized === 'header' || normalized === 'hero') {
    return 'header';
  }
  if (normalized === 'footer') {
    return 'footer';
  }
  if (normalized === 'column' || normalized === 'sidebar') {
    return 'column';
  }
  if (normalized === 'menu' || normalized === 'navigation') {
    return 'menu';
  }
  return 'main';
};

export const getDefaultScopeForArea = (areaType?: string) => {
  const normalized = (areaType || '').toLowerCase();
  if (normalized === 'column' || normalized === 'sidebar') {
    return ['column', 'universal'];
  }
  return ['primary', 'universal'];
};

export const normalizeScopeList = (scope?: string[], fallback: string[] = ['primary', 'universal']) => {
  if (Array.isArray(scope) && scope.length > 0) {
    const cleaned = scope
      .map(value => (value || '').toLowerCase())
      .filter(value => ['primary', 'column', 'universal'].includes(value));
    return cleaned.length > 0 ? Array.from(new Set(cleaned)) : fallback;
  }
  return fallback;
};

type ApiErrorLike = {
  response?: {
    data?: {
      error?: string;
      message?: string;
    };
  };
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as ApiErrorLike;
  return maybe?.response?.data?.error || maybe?.response?.data?.message || fallback;
};

interface ContainerEditorProps {
  initialTab?: 'general' | 'rules' | 'components';
  containerId?: number; // ID контейнера для редактирования (если есть)
  templateId?: number; // ID HTML шаблона (для создания нового контейнера)
  onSave?: (container: TemplateContainer) => void;
  onCancel?: () => void;
  onClose?: () => void;
}

interface ComponentType {
  component_name: string;
  variant_code: string;
  description?: string;
}

type SelectionMode = 'category' | 'specific';

const ContainerEditor: React.FC<ContainerEditorProps> = ({
  initialTab = 'general',
  containerId,
  templateId,
  onSave,
  onCancel,
  onClose
}) => {
  // Состояния для данных контейнера
  const [container, setContainer] = useState<Partial<TemplateContainer>>({
    name: '',
    description: '',
    container_type: 'custom',
    placement_type: 'any',
    max_components: 10,
    min_components: 1,
    icon: 'default',
    color: '#007bff',
    is_active: true,
    area_type: 'main',
    allowed_component_scope: ['primary', 'universal'],
    requires_menu_component: false,
    disallow_menu_components: false,
    is_published: true,
    settings: {},
    visual_style: {}
  });

  // Состояния для шаблонов контейнеров
  const [containerTemplates, setContainerTemplates] = useState<ContainerTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // Состояния для правил компонентов
  const [componentRules, setComponentRules] = useState<ContainerComponentRule[]>([]);
  const [availableComponents, setAvailableComponents] = useState<ComponentType[]>([]);
  const [newRuleCategory, setNewRuleCategory] = useState<string>('');
  const [newRuleMode, setNewRuleMode] = useState<SelectionMode>('category');
  const [newRuleComponent, setNewRuleComponent] = useState<string>('');
  
  // Состояния для UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'rules' | 'components'>(initialTab);

  const normalizeContainer = useCallback((data: TemplateContainer) => {
    const baseArea = data.area_type || deriveAreaType(data.container_type);
    return {
      ...data,
      area_type: baseArea,
      allowed_component_scope: normalizeScopeList(
        data.allowed_component_scope,
        getDefaultScopeForArea(baseArea)
      ),
      requires_menu_component: data.requires_menu_component === true,
      disallow_menu_components: data.disallow_menu_components === true,
      is_published: data.is_published !== false,
      settings: data.settings || {},
      visual_style: data.visual_style || {}
    };
  }, []);

  const toggleScope = useCallback((scope: string) => {
    setContainer(prev => {
      const current = prev.allowed_component_scope || [];
      const exists = current.includes(scope);
      let nextScopes = exists
        ? current.filter(item => item !== scope)
        : [...current, scope];

      if (nextScopes.length === 0) {
        nextScopes = getDefaultScopeForArea(prev.area_type);
      }

      return {
        ...prev,
        allowed_component_scope: nextScopes
      };
    });
  }, []);

  const handleRequiresMenuChange = useCallback((value: boolean) => {
    setContainer(prev => ({
      ...prev,
      requires_menu_component: value,
      disallow_menu_components: value ? false : prev.disallow_menu_components
    }));
  }, []);

  const handleDisallowMenuChange = useCallback((value: boolean) => {
    setContainer(prev => ({
      ...prev,
      disallow_menu_components: value,
      requires_menu_component: value ? false : prev.requires_menu_component
    }));
  }, []);

  const loadContainerTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/container-templates?active=true', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContainerTemplates(response.data);
    } catch (error) {
      console.error('Error loading container templates:', error);
    }
  }, []);

  const loadAvailableComponents = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/components', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Преобразуем данные компонентов в нужный формат
      const components = response.data.map((comp: any) => ({
        component_name: comp.component_name,
        variant_code: comp.variant_code,
        description: comp.description
      }));
      
      setAvailableComponents(components);
    } catch (error) {
      console.error('Error loading available components:', error);
    }
  }, []);

  const loadContainerData = useCallback(async () => {
    if (!containerId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/admin/containers/${containerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContainer(normalizeContainer(response.data));
    } catch (error) {
      console.error('Error loading container data:', error);
      setError('Ошибка загрузки данных контейнера');
    }
  }, [containerId, normalizeContainer]);

  const loadComponentRules = useCallback(async () => {
    if (!containerId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/admin/containers/${containerId}/component-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComponentRules(response.data);
    } catch (error) {
      console.error('Error loading component rules:', error);
    }
  }, [containerId]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Загружаем шаблоны контейнеров
      await loadContainerTemplates();
      
      // Загружаем доступные компоненты
      await loadAvailableComponents();
      
      // Если редактируем существующий контейнер
      if (containerId) {
        await loadContainerData();
        await loadComponentRules();
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [containerId, loadAvailableComponents, loadComponentRules, loadContainerData, loadContainerTemplates]);

  // Загрузка данных при инициализации
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Обработчики изменения данных
  const handleInputChange = useCallback((field: string, value: any) => {
    setContainer(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleVisualStyleChange = useCallback((newStyle: any) => {
    setContainer(prev => ({
      ...prev,
      visual_style: { ...prev.visual_style, ...newStyle }
    }));
  }, []);

  const componentCategories = useMemo(() => {
    const map = new Map<string, { value: string; label: string }[]>();
    availableComponents.forEach(comp => {
      const slug = comp.component_name;
      if (!slug) {
        return;
      }
      const category = slug.split('-')[0] || slug;
      if (!map.has(category)) {
        map.set(category, []);
      }
      const entry = map.get(category)!;
      if (!entry.some(item => item.value === slug)) {
        entry.push({
          value: slug,
          label: `${slug}${comp.variant_code ? ` (${comp.variant_code})` : ''}`
        });
      }
    });
    map.forEach(list => list.sort((a, b) => a.value.localeCompare(b.value)));
    return map;
  }, [availableComponents]);

  const categoryOptions = useMemo(
    () => Array.from(componentCategories.keys()).sort(),
    [componentCategories]
  );

  const takenRandomCategories = useMemo(() => {
    const set = new Set<string>();
    componentRules.forEach(rule => {
      const mode = (rule.selection_mode as SelectionMode) || (rule.component_name ? 'specific' : 'category');
      if (mode === 'category') {
        set.add(rule.component_type);
      }
    });
    return set;
  }, [componentRules]);

  // Применение шаблона контейнера
  const applyTemplate = async (templateId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/admin/container-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const template = response.data;
      const templateArea = template.area_type || deriveAreaType(template.container_type);
      const templateScopes = normalizeScopeList(
        template.allowed_component_scope,
        getDefaultScopeForArea(templateArea)
      );
      
      setContainer(prev => ({
        ...prev,
        container_type: template.container_type,
        placement_type: template.placement_type,
        max_components: template.max_components,
        min_components: template.min_components,
        icon: template.icon,
        color: template.color,
        area_type: templateArea,
        allowed_component_scope: templateScopes,
        requires_menu_component: template.requires_menu_component === true,
        disallow_menu_components: template.disallow_menu_components === true,
        is_published: template.is_published !== undefined ? template.is_published : prev.is_published,
        settings: { ...prev.settings, ...template.default_settings },
        visual_style: { ...prev.visual_style, ...template.default_visual_style }
      }));
      
      setSelectedTemplateId(templateId);
      console.log(`✅ Применен шаблон: ${template.name}`);
    } catch (error) {
      console.error('Error applying template:', error);
      setError('Ошибка применения шаблона');
    }
  };

  const currentAreaType = container.area_type || deriveAreaType(container.container_type);
  const currentScopes = container.allowed_component_scope && container.allowed_component_scope.length > 0
    ? container.allowed_component_scope
    : getDefaultScopeForArea(currentAreaType);

  // Сохранение контейнера
  const handleSave = async () => {
    if (!container.name?.trim()) {
      setError('Название контейнера обязательно');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        name: container.name.trim(),
        description: container.description || '',
        container_type: container.container_type || 'custom',
        placement_rules: container.placement_type || 'any',
        min_components: container.min_components ?? 1,
        max_components: container.max_components || 10,
        is_system: container.is_system || false,
        is_multiple: container.is_multiple !== undefined ? container.is_multiple : true,
        sort_order: container.sort_order || 100,
        icon: container.icon || 'default',
        color: container.color || '#007bff',
        area_type: currentAreaType,
        allowed_component_scope: currentScopes,
        requires_menu_component: container.requires_menu_component === true,
        disallow_menu_components: container.disallow_menu_components === true,
        is_published: container.is_published !== false,
        settings: container.settings || {},
        visual_style: container.visual_style || {}
      };

      if (containerId) {
        const response = await axios.put(
          `/api/admin/containers/${containerId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        onSave?.(response.data);
        console.log('✅ Контейнер обновлен');
      } else if (selectedTemplateId && templateId) {
        const response = await axios.post(
          '/api/admin/containers/create-from-template',
          {
            template_id: selectedTemplateId,
            html_template_id: templateId,
            name: payload.name,
            description: payload.description,
            custom_settings: payload.settings,
            custom_visual_style: payload.visual_style,
            area_type: payload.area_type,
            allowed_component_scope: payload.allowed_component_scope,
            requires_menu_component: payload.requires_menu_component,
            disallow_menu_components: payload.disallow_menu_components,
            is_published: payload.is_published
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        onSave?.(response.data);
        console.log('✅ Контейнер создан из шаблона');
      } else {
        setError('Выберите шаблон для создания контейнера');
        return;
      }
      
      onClose?.();
    } catch (error: any) {
      console.error('Error saving container:', error);
      setError(getApiErrorMessage(error, 'Ошибка сохранения контейнера'));
    } finally {
      setSaving(false);
    }
  };

  // Добавление правила компонента
  const addComponentRule = async (
    category: string,
    mode: SelectionMode,
    specificComponent?: string
  ) => {
    if (!containerId) return;
    if (!category) {
      setError('Выберите категорию компонента');
      return;
    }
    if (mode === 'specific' && !specificComponent) {
      setError('Выберите конкретный компонент');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/admin/containers/${containerId}/component-rules`,
        {
          component_type: category,
          selection_mode: mode,
          component_name: mode === 'specific' ? specificComponent : undefined,
          is_allowed: true,
          is_required: false,
          min_instances: 0,
          max_instances: container.max_components || 10
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setComponentRules(prev => [...prev, response.data]);
      setNewRuleCategory('');
      setNewRuleComponent('');
      setNewRuleMode('category');
      console.log(`✅ Добавлено правило для компонента: ${category} (${mode})`);
    } catch (error: any) {
      console.error('Error adding component rule:', error);
      setError(getApiErrorMessage(error, 'Ошибка добавления правила компонента'));
    }
  };

  const handleAddRule = () => {
    addComponentRule(
      newRuleCategory,
      newRuleMode,
      newRuleMode === 'specific' ? newRuleComponent : undefined
    );
  };

  // Обновление правила компонента
  const updateComponentRule = async (ruleId: number, updates: Partial<ContainerComponentRule>) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `/api/admin/container-component-rules/${ruleId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setComponentRules(prev => 
        prev.map(rule => rule.id === ruleId ? response.data : rule)
      );
      console.log(`✅ Обновлено правило компонента`);
    } catch (error: any) {
      console.error('Error updating component rule:', error);
      setError(getApiErrorMessage(error, 'Ошибка обновления правила'));
    }
  };

  // Удаление правила компонента
  const deleteComponentRule = async (ruleId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/admin/container-component-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setComponentRules(prev => prev.filter(rule => rule.id !== ruleId));
      console.log(`✅ Удалено правило компонента`);
    } catch (error: any) {
      console.error('Error deleting component rule:', error);
      setError(getApiErrorMessage(error, 'Ошибка удаления правила'));
    }
  };

  // Функции для изменения порядка компонентов
  const moveComponentUp = (index: number) => {
    if (index <= 0) return;
    
    const newRules = [...componentRules];
    [newRules[index - 1], newRules[index]] = [newRules[index], newRules[index - 1]];
    setComponentRules(newRules);
    
    // Обновляем sort_order в базе данных
    updateComponentOrder(newRules);
  };

  const moveComponentDown = (index: number) => {
    if (index >= componentRules.length - 1) return;
    
    const newRules = [...componentRules];
    [newRules[index], newRules[index + 1]] = [newRules[index + 1], newRules[index]];
    setComponentRules(newRules);
    
    // Обновляем sort_order в базе данных
    updateComponentOrder(newRules);
  };

  const updateComponentOrder = async (orderedRules: ContainerComponentRule[]) => {
    try {
      const token = localStorage.getItem('token');
      
      // Обновляем sort_order для каждого правила
      const updates = orderedRules.map((rule, index) => ({
        id: rule.id,
        sort_order: index
      }));
      
      await axios.put(
        `/api/admin/containers/${containerId}/component-rules/reorder`,
        { rules: updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ Порядок компонентов обновлен');
    } catch (error: any) {
      console.error('Error updating component order:', error);
      // В случае ошибки восстанавливаем предыдущий порядок
      loadComponentRules();
    }
  };

  const getRuleSelectionMode = (rule: ContainerComponentRule): SelectionMode =>
    (rule.selection_mode as SelectionMode) || (rule.component_name ? 'specific' : 'category');

  const handleRuleModeChange = (rule: ContainerComponentRule, mode: SelectionMode) => {
    if (!rule.id) return;
    if (mode === 'specific') {
      const options = componentCategories.get(rule.component_type) || [];
      if (!options.length) {
        setError('Для этой категории нет доступных компонентов');
        return;
      }
      updateComponentRule(rule.id, {
        selection_mode: mode,
        component_name: options[0].value
      });
    } else {
      updateComponentRule(rule.id, {
        selection_mode: mode,
        component_name: null
      });
    }
  };

  const handleRuleComponentChange = (rule: ContainerComponentRule, slug: string) => {
    if (!rule.id || !slug) return;
    updateComponentRule(rule.id, {
      selection_mode: 'specific',
      component_name: slug
    });
  };

  if (loading) {
    return (
      <div className="container-editor">
        <div className="container-editor__loading">
          <div className="spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-editor">
      <div className="container-editor__header">
        <h2>
          {containerId ? 'Редактирование контейнера' : 'Создание контейнера'}
        </h2>
        <button 
          className="container-editor__close"
          onClick={onClose}
          aria-label="Закрыть"
        >
          ×
        </button>
      </div>

      {error && (
        <div className="container-editor__error">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="container-editor__tabs">
        <button 
          className={`tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          Основные настройки
        </button>
        <button 
          className={`tab ${activeTab === 'rules' ? 'active' : ''}`}
          onClick={() => setActiveTab('rules')}
        >
          Правила размещения
        </button>
        <button 
          className={`tab ${activeTab === 'components' ? 'active' : ''}`}
          onClick={() => setActiveTab('components')}
        >
          Компоненты {containerId ? `(${componentRules.length})` : '(настройка после сохранения)'}
        </button>
      </div>

      <div className="container-editor__content">
        {activeTab === 'general' && (
          <div className="container-editor__general">
            {!containerId && (
              <div className="form-group">
                <label>Шаблон контейнера:</label>
                <select
                  value={selectedTemplateId || ''}
                  onChange={(e) => {
                    const templateId = parseInt(e.target.value);
                    if (templateId) {
                      applyTemplate(templateId);
                    }
                  }}
                  required
                >
                  <option value="">Выберите шаблон...</option>
                  {containerTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.container_type})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Название контейнера: *</label>
              <input
                type="text"
                value={container.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Введите название контейнера"
                required
              />
            </div>

            <div className="form-group">
              <label>Описание:</label>
              <textarea
                value={container.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Описание назначения контейнера"
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Тип размещения:</label>
                <select
                  value={container.placement_type || 'any'}
                  onChange={(e) => handleInputChange('placement_type', e.target.value)}
                >
                  <option value="any">Любое размещение</option>
                  <option value="main-only">Только в основной области</option>
                  <option value="sidebar-only">Только в боковых колонках</option>
                  <option value="top">Только сверху</option>
                  <option value="bottom">Только снизу</option>
                </select>
              </div>

              <div className="form-group">
                <label>Иконка:</label>
                <input
                  type="text"
                  value={container.icon || 'default'}
                  onChange={(e) => handleInputChange('icon', e.target.value)}
                  placeholder="default"
                />
              </div>
            </div>

          <div className="form-row">
            <div className="form-group">
              <label>Область контейнера:</label>
              <select
                value={currentAreaType}
                onChange={(e) => handleInputChange('area_type', e.target.value)}
              >
                {areaOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Допустимые компоненты:</label>
              <div className="scope-checkboxes">
                {scopeOptions.map(option => (
                  <label key={option.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={currentScopes.includes(option.value)}
                      onChange={() => toggleScope(option.value)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <small>Универсальные компоненты подходят для любой области</small>
            </div>
          </div>

            <div className="form-row">
              <div className="form-group">
                <label>Мин. компонентов:</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={container.min_components || 1}
                  onChange={(e) => handleInputChange('min_components', parseInt(e.target.value))}
                />
              </div>

              <div className="form-group">
                <label>Макс. компонентов:</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={container.max_components || 10}
                  onChange={(e) => handleInputChange('max_components', parseInt(e.target.value))}
                />
              </div>

              <div className="form-group">
                <label>Цвет:</label>
                <input
                  type="color"
                  value={container.color || '#007bff'}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                />
              </div>
            </div>

          <div className="form-row">
            <div className="form-group toggle-group">
              <label>Опубликован</label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={container.is_published !== false}
                  onChange={(e) => handleInputChange('is_published', e.target.checked)}
                />
                <span className="slider"></span>
              </label>
              <small>Неопубликованные контейнеры доступны только администраторам</small>
            </div>

            <div className="form-group toggle-group">
              <label>Требует меню после себя</label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={container.requires_menu_component === true}
                  onChange={(e) => handleRequiresMenuChange(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
              <small>Показывать напоминание, если контейнер без меню</small>
            </div>

            <div className="form-group toggle-group">
              <label>Запретить отдельные меню</label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={container.disallow_menu_components === true}
                  onChange={(e) => handleDisallowMenuChange(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
              <small>Используется для хедеров с уже встроенным меню</small>
            </div>
          </div>

          {currentAreaType === 'header' && (
            <div className="hint-box">
              <strong>Совет:</strong> после хедеров без меню добавляйте компонент меню или включайте опцию напоминания.
            </div>
          )}
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="container-editor__rules">
            <h3>Правила размещения компонентов</h3>
            
            <div className="rules-info">
              <p>
                <strong>Текущие ограничения:</strong> от {container.min_components || 1} до {container.max_components || 10} компонентов
              </p>
              <p>
                <strong>Размещение:</strong> {
                  container.placement_type === 'any' ? 'В любом месте' :
                  container.placement_type === 'main-only' ? 'Только в основной области' :
                  container.placement_type === 'sidebar-only' ? 'Только в боковых колонках' :
                  container.placement_type === 'top' ? 'Только сверху' :
                  container.placement_type === 'bottom' ? 'Только снизу' : 
                  container.placement_type
                }
              </p>
            </div>

            <div className="visual-settings">
              <h4>Визуальные настройки</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Отступы (padding):</label>
                  <input
                    type="text"
                    value={container.visual_style?.padding || ''}
                    onChange={(e) => handleVisualStyleChange({ padding: e.target.value })}
                    placeholder="10px 15px"
                  />
                </div>
                <div className="form-group">
                  <label>Отступы снаружи (margin):</label>
                  <input
                    type="text"
                    value={container.visual_style?.margin || ''}
                    onChange={(e) => handleVisualStyleChange({ margin: e.target.value })}
                    placeholder="5px 0"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Граница (border):</label>
                  <input
                    type="text"
                    value={container.visual_style?.border || ''}
                    onChange={(e) => handleVisualStyleChange({ border: e.target.value })}
                    placeholder="1px solid #ccc"
                  />
                </div>
                <div className="form-group">
                  <label>Скругление углов:</label>
                  <input
                    type="text"
                    value={container.visual_style?.borderRadius || ''}
                    onChange={(e) => handleVisualStyleChange({ borderRadius: e.target.value })}
                    placeholder="4px"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'components' && (
          <div className="container-editor__components">
            {!containerId ? (
              // Для нового контейнера - показываем предупреждение
              <div className="components-placeholder">
                <div className="placeholder-icon">📋</div>
                <h3>Назначение компонентов</h3>
                <p>
                  Сначала сохраните контейнер, чтобы настроить какие компоненты 
                  могут быть использованы в этом контейнере.
                </p>
                <div className="placeholder-info">
                  <h4>После сохранения вы сможете:</h4>
                  <ul>
                    <li>✅ Выбрать разрешенные компоненты из списка</li>
                    <li>✅ Пометить обязательные компоненты</li>
                    <li>✅ Настроить количество экземпляров</li>
                    <li>✅ Создать правила размещения</li>
                  </ul>
                </div>
                <button 
                  className="btn btn-primary"
                  onClick={() => setActiveTab('general')}
                >
                  Перейти к основным настройкам
                </button>
              </div>
            ) : (
              // Для существующего контейнера - показываем интерфейс назначения
              <>
                <div className="components-header">
                  <h3>Разрешенные компоненты</h3>
                  <p>Выберите какие компоненты могут использоваться в этом контейнере</p>
                  <div className="component-add-form">
                    <div className="form-group">
                      <label>Категория</label>
                      <select
                        value={newRuleCategory}
                        onChange={(e) => {
                          setNewRuleCategory(e.target.value);
                          setNewRuleComponent('');
                        }}
                      >
                        <option value="">Выберите категорию…</option>
                        {categoryOptions.map(category => (
                          <option
                            key={category}
                            value={category}
                            disabled={newRuleMode === 'category' && takenRandomCategories.has(category)}
                          >
                            {category}
                            {takenRandomCategories.has(category) && ' (уже используется)'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Режим выбора</label>
                      <select
                        value={newRuleMode}
                        onChange={(e) => {
                          const mode = e.target.value as SelectionMode;
                          setNewRuleMode(mode);
                          if (mode === 'category') {
                            setNewRuleComponent('');
                          }
                        }}
                      >
                        <option value="category">Случайно из категории</option>
                        <option value="specific">Конкретный компонент</option>
                      </select>
                    </div>
                    {newRuleMode === 'specific' && (
                      <div className="form-group">
                        <label>Компонент</label>
                        <select
                          value={newRuleComponent}
                          onChange={(e) => setNewRuleComponent(e.target.value)}
                          disabled={!newRuleCategory || !(componentCategories.get(newRuleCategory)?.length)}
                        >
                          <option value="">Выберите компонент…</option>
                          {(componentCategories.get(newRuleCategory) || []).map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <button
                      className="btn btn-primary"
                      onClick={() => handleAddRule()}
                      disabled={
                        !newRuleCategory ||
                        (newRuleMode === 'category' && takenRandomCategories.has(newRuleCategory)) ||
                        (newRuleMode === 'specific' && (!newRuleComponent || !newRuleCategory))
                      }
                    >
                      Добавить правило
                    </button>
                  </div>
            </div>

            <div className="component-rules-list">
              <p className="u2-muted">Изменения правил сохраняются сразу. Кнопка «Сохранить» применяет основные настройки контейнера.</p>
              {componentRules.map((rule, index) => (
                <div key={rule.id} className="component-rule">
                  <div className="component-rule__header">
                    <h4>
                      {rule.component_type}
                      {getRuleSelectionMode(rule) === 'specific' && rule.component_name ? (
                        <span className="component-rule__specific-label"> → {rule.component_name}</span>
                      ) : null}
                    </h4>
                    <div className="component-rule__controls">
                      <div className="move-buttons">
                        <button
                          className="move-btn move-up"
                          onClick={() => moveComponentUp(index)}
                          disabled={index === 0}
                          title="Переместить вверх"
                        >
                          ↑
                        </button>
                        <button
                          className="move-btn move-down"
                          onClick={() => moveComponentDown(index)}
                          disabled={index === componentRules.length - 1}
                          title="Переместить вниз"
                        >
                          ↓
                        </button>
                      </div>
                      <button
                        className="delete-btn"
                        onClick={() => rule.id && deleteComponentRule(rule.id)}
                        title="Удалить компонент"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  
                  <div className="component-rule__settings">
                    <div className="mode-select">
                      <label>Режим:</label>
                      <select
                        value={getRuleSelectionMode(rule)}
                        onChange={(e) => handleRuleModeChange(rule, e.target.value as SelectionMode)}
                      >
                        <option value="category">Случайно из категории</option>
                        <option value="specific">Конкретный компонент</option>
                      </select>
                    </div>
                    
                    {getRuleSelectionMode(rule) === 'specific' && (
                      <div className="mode-select">
                        <label>Компонент:</label>
                        <select
                          value={rule.component_name || ''}
                          onChange={(e) => handleRuleComponentChange(rule, e.target.value)}
                        >
                          {(componentCategories.get(rule.component_type) || []).map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <label>
                      <input
                        type="checkbox"
                        checked={rule.is_allowed}
                        onChange={(e) => rule.id && updateComponentRule(rule.id, { is_allowed: e.target.checked })}
                      />
                      Разрешен
                    </label>
                    
                    <label>
                      <input
                        type="checkbox"
                        checked={rule.is_required}
                        onChange={(e) => rule.id && updateComponentRule(rule.id, { is_required: e.target.checked })}
                      />
                      Обязателен
                    </label>
                    
                    <div className="instances">
                      <label>
                        Мин:
                        <input
                          type="number"
                          min="0"
                          value={rule.min_instances || 0}
                          onChange={(e) => rule.id && updateComponentRule(rule.id, { min_instances: parseInt(e.target.value) })}
                        />
                      </label>
                      
                      <label>
                        Макс:
                        <input
                          type="number"
                          min="1"
                          value={rule.max_instances || 10}
                          onChange={(e) => rule.id && updateComponentRule(rule.id, { max_instances: parseInt(e.target.value) })}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              
                  {componentRules.length === 0 && (
                    <div className="no-rules">
                      <div className="no-rules-icon">📝</div>
                      <h4>Компоненты не назначены</h4>
                      <p>Используйте выпадающий список выше для добавления разрешенных компонентов.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="container-editor__footer">
        <button 
          className="btn btn-secondary"
          onClick={onCancel || onClose}
          disabled={saving}
        >
          Отмена
        </button>
        <button 
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !container.name?.trim()}
        >
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
};

export default ContainerEditor;
